import { config } from '../appConfig'
import { BTCNFT } from '../interfaces/common'

export class OrdinalsFetchError extends Error {
  constructor(public readonly cause: string) {
    super(`Failed to fetch inscriptions: ${cause}`)
    this.name = 'OrdinalsFetchError'
  }
}

const ORDISCAN_API_URL = 'https://api.ordiscan.com/v1'

interface OrdiscanInscription {
  inscription_id: string
  inscription_number: number
  content_type: string
  owner_address: string
  owner_output: string
  timestamp: string
  metadata: Record<string, string | number> | null
  satributes: string[]
  content_url: string
  collection_slug: string | null
  sats_name: string | null
}

interface OrdiscanResponse {
  data: OrdiscanInscription[]
}

class OrdinalsClient {
  static mapApiToBTCNFT(inscription: OrdiscanInscription): BTCNFT {
    const [txId, vout] = inscription.owner_output.split(':')

    const metadata = inscription.metadata
      ? Object.fromEntries(Object.entries(inscription.metadata).map(([k, v]) => [k, String(v)]))
      : undefined

    return {
      id: inscription.inscription_id,
      txId,
      name: metadata?.Name || `Ordinal: ${inscription.inscription_number}`,
      metadata,
      number: inscription.inscription_number,
      address: inscription.owner_address,
      contentType: inscription.content_type,
      satRarity: inscription.satributes.length > 0 ? inscription.satributes[0] : 'common',
      genesisTimestamp: new Date(inscription.timestamp).getTime(),
      standard: 'ordinal',
      url: inscription.content_url,
      collection: inscription.collection_slug ?? `ordinal-${inscription.inscription_number}`,
      mintIndex: parseInt(vout),
    }
  }

  async getOrdinals(address: string): Promise<BTCNFT[]> {
    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${config.fetchEnv('ORDISCAN_KEY')}`,
    }

    let response: Response
    try {
      response = await fetch(`${ORDISCAN_API_URL}/address/${address}/inscriptions`, { headers })
    } catch (e) {
      throw new OrdinalsFetchError((e as Error)?.message || 'request failed')
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new OrdinalsFetchError(`HTTP ${response.status}${body ? `: ${body}` : ''}`)
    }

    const data: OrdiscanResponse = await response.json()
    return data.data.map(OrdinalsClient.mapApiToBTCNFT)
  }
}

export default OrdinalsClient
